import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { SmartImage } from "@/components/SmartImage";
import { AboutMe } from "@/components/AboutMe";
import { getIcon } from "@/data/site";
import { submitLead } from "@/lib/api";

const emptyForm = { name: "", email: "", phone: "", city: "", message: "", website: "" };

const validate = (f) => {
  const e = {};
  if (!f.name || f.name.trim().length < 2) e.name = "Pakilagay ang pangalan mo";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "Maglagay ng valid na email";
  if (!f.phone || f.phone.trim().length < 7) e.phone = "Maglagay ng valid na numero";
  return e;
};

export const BusinessOpportunity = ({ opportunity = {}, aboutMe = {}, config = {} }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const benefits = opportunity.benefits || [];
  const steps = opportunity.steps || [];
  const showAboutMe = aboutMe && aboutMe.enabled !== false;

  const set = (k) => (ev) => setForm((s) => ({ ...s, [k]: ev.target.value }));

  // "Sali sa team ko" -> bring the form into view and focus the first field
  const focusForm = () => {
    const card = document.getElementById("join-form");
    if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => document.getElementById("j-name")?.focus({ preventScroll: true }), 450);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const eobj = validate(form);
    setErrors(eobj);
    if (Object.keys(eobj).length) return;
    setSubmitting(true);
    try {
      await submitLead(form);
      setDone(true);
      toast.success("Na-send ang application! Kokontakin kita agad.");
      setForm(emptyForm);
    } catch (err) {
      toast.error("May nangyaring mali. Pakisubukang muli.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="join" className="scroll-anchor bg-primary text-primary-foreground">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        {/* Row 1: intro + benefits + steps */}
        <div>
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-wider text-accent">{opportunity.eyebrow}</p>
            <h2 className="font-display mt-2 max-w-3xl text-3xl font-semibold tracking-tight sm:text-4xl">{opportunity.heading}</h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-primary-foreground/80">{opportunity.body}</p>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((b, i) => {
              const Icon = getIcon(b.icon);
              return (
                <Reveal key={b.title || i} delay={i * 0.06}>
                  <div className="flex h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-white/5" data-testid={`opportunity-benefit-${i}`}>
                    {b.image ? (
                      <div className="relative aspect-[16/9] w-full overflow-hidden bg-white/10" data-testid={`opportunity-benefit-image-${i}`}>
                        <SmartImage src={b.image} alt={b.title} fit="cover" fallbackLabel={b.title} />
                        <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground shadow-[0_6px_18px_-6px_rgba(0,0,0,0.5)]">
                          <Icon className="h-5 w-5" />
                        </span>
                      </div>
                    ) : null}
                    <div className="flex flex-1 gap-3 p-4">
                      {!b.image ? (
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                          <Icon className="h-5 w-5" />
                        </span>
                      ) : null}
                      <div>
                        <h3 className="text-sm font-semibold">{b.title}</h3>
                        <p className="mt-1 text-sm text-primary-foreground/70">{b.desc}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-primary-foreground/60">{opportunity.stepsTitle}</p>
            <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
              {steps.map((s, i) => (
                <div key={s.step || i}>
                  <span className="font-display text-2xl font-semibold text-accent">{s.step}</span>
                  <h4 className="mt-1 text-base font-semibold">{s.title}</h4>
                  <p className="mt-1 text-sm text-primary-foreground/70">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: personal story beside the Join form */}
        <div className={`mt-14 grid grid-cols-1 gap-8 lg:mt-16 lg:items-stretch lg:gap-12 ${showAboutMe ? "lg:grid-cols-2" : ""}`}>
          {showAboutMe ? (
            <Reveal className="h-full">
              <AboutMe aboutMe={aboutMe} config={config} onCta={focusForm} />
            </Reveal>
          ) : null}

          <div id="join-form" className={showAboutMe ? "" : "mx-auto w-full max-w-2xl"}>
            <Card className="rounded-2xl border-border bg-card p-6 text-card-foreground shadow-xl sm:p-8 lg:h-full" data-testid="join-team-form-card">
              {done ? (
                <div className="flex flex-col items-center py-8 text-center">
                  <CheckCircle2 className="h-14 w-14 text-primary" />
                  <h3 className="font-display mt-4 text-2xl font-semibold text-foreground">{opportunity.successHeading}</h3>
                  <p className="mt-2 max-w-sm text-sm text-muted-foreground">{opportunity.successBody}</p>
                  <Button className="mt-6" variant="outline" onClick={() => setDone(false)} data-testid="join-reset-button">Mag-submit ulit</Button>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-2xl font-semibold text-foreground">{opportunity.formHeading}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{opportunity.formSub}</p>
                  <form onSubmit={handleSubmit} className="mt-5 space-y-4" noValidate>
                    <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
                    <div>
                      <Label htmlFor="j-name">Buong pangalan</Label>
                      <Input id="j-name" className="mt-1.5 h-11 bg-white" value={form.name} onChange={set("name")} placeholder="Juan Dela Cruz" data-testid="join-name-input" />
                      {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="j-email">Email</Label>
                        <Input id="j-email" type="email" className="mt-1.5 h-11 bg-white" value={form.email} onChange={set("email")} placeholder="ikaw@example.com" data-testid="join-email-input" />
                        {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
                      </div>
                      <div>
                        <Label htmlFor="j-phone">Numero</Label>
                        <Input id="j-phone" className="mt-1.5 h-11 bg-white" value={form.phone} onChange={set("phone")} placeholder="+63 9XX XXX XXXX" data-testid="join-phone-input" />
                        {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="j-city">Lungsod</Label>
                      <Input id="j-city" className="mt-1.5 h-11 bg-white" value={form.city} onChange={set("city")} placeholder="Saan ka nakabase?" data-testid="join-city-input" />
                    </div>
                    <div>
                      <Label htmlFor="j-msg">Bakit mo gustong sumali? (optional)</Label>
                      <Textarea id="j-msg" className="mt-1.5 min-h-24 bg-white" value={form.message} onChange={set("message")} placeholder="Kwentuhan mo ako about sa goals mo" data-testid="join-message-input" />
                    </div>
                    <Button type="submit" className="h-11 w-full" disabled={submitting} data-testid="join-form-submit-button">
                      {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sina-send...</>) : "Mag-apply para sumali"}
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">Sa pag-apply, pumapayag kang kontakin about sa opportunity. Walang spam, promise.</p>
                  </form>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
